import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useConfirmationModal } from 'shared/components/ConfirmationModal';
import { IComposite, INITIAL_COMPOSITE_STATES } from 'shared/constants/INITIAL_COMPOSITE_STATES';
import { profileService } from 'shared/services/profileService';
import { uploadService } from 'shared/services/uploadService';

const { updateProfile } = profileService();
const { uploadMedia } = uploadService();

export const useEditProfileCompositeCardMutation = ({
  compositeCardData,
  isLoadingCompositeCardData,
  profileId,
  profileInfo,
  setShowEditInfo,
}: any) => {
  const queryClient = useQueryClient();
  const {
    ConfirmationModal: ConfirmationModal,
    handleOpen: handleOpenConfirm,
    handleSetMessage: handleConfirmSetMessage,
    isOpen: isConfirmModalOpen,
  } = useConfirmationModal({
    type: 'warning',
  });
  const [isInitialCompositeDataIsNull, setIsInitialCompositeDataIsNull] = useState(true);

  const { mutateAsync: updateProfileMutateAsync, isLoading: isLoadingUpdateProfile } = updateProfile();

  const updateCompCard = useCallback(
    async (val: IComposite) => {
      await updateProfileMutateAsync({
        profileId,
        payload: {
          composite_card: {
            ...compositeCardData?.data.attributes.composite_card,
            ...val,
          },
        },
      });
    },
    [compositeCardData?.data.attributes.composite_card, profileId, updateProfileMutateAsync],
  );

  const handleSave = async (
    val: Array<{
      key: string;
      value: string;
    }>,
  ) => {
    await updateCompCard({
      info: val,
    });
    await queryClient.invalidateQueries(['talents/statistics']);
    setShowEditInfo(false);
    await queryClient.invalidateQueries(['profile_tab']);
    await queryClient.invalidateQueries(['my_data']);
  };

  const toggleEnable = useCallback(async () => {
    const isNonRepre = profileInfo?.representation_type !== 'agency_representation';
    const toggleCompositeCard = async () => {
      const currentValue = compositeCardData?.data.attributes.composite_card?.enabled;
      await updateCompCard({
        enabled: !currentValue,
      });
      await queryClient.invalidateQueries(['profile_tab', profileId, 'composite_card'], {
        refetchInactive: true,
      });
      await queryClient.invalidateQueries(['id', profileId], {
        refetchInactive: true,
      });
    };
    if (isNonRepre) {
      await toggleCompositeCard();
    } else {
      await toggleCompositeCard();
    }
  }, [compositeCardData, profileId, queryClient, updateCompCard, profileInfo?.representation_type]);

  const handleClick = () => {
    handleOpenConfirm();
    handleConfirmSetMessage(
      'Exit Confirmation',
      `You have unsaved work in progress, this will discard all the changes you made. Do you wish to continue?`,
      'Yes',
    );
    return;
  };

  useEffect(() => {
    if (isLoadingCompositeCardData === true) return;
    if (compositeCardData?.data.attributes.composite_card === null) {
      setIsInitialCompositeDataIsNull(true);
      const update = async () => {
        await updateCompCard(INITIAL_COMPOSITE_STATES);
        await queryClient.invalidateQueries(['profile_tab', profileId, 'composite_card'], {
          refetchInactive: true,
        });
      };
      update();
    } else {
      setIsInitialCompositeDataIsNull(false);
    }
  }, [isLoadingCompositeCardData, compositeCardData, updateCompCard, profileId, queryClient]);

  return {
    updateCompCard,
    updateProfile,
    ConfirmationModal,
    isConfirmModalOpen,
    handleClick,
    toggleEnable,
    handleSave,
    uploadMedia,
    isInitialCompositeDataIsNull,
    isLoadingUpdateProfile,
  };
};
